import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Instagram,
  Facebook,
  MessageCircle,
  Check,
  CalendarDays,
  Sun,
  Sunset,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import BranchPickerModal from "@/components/site/BranchPickerModal";
import BranchLocator from "@/components/site/BranchLocator";

const visitPerks = [
  "Conoce a los profesores en persona",
  "Recorre las aulas e instalaciones",
  "Resuelve todas tus dudas",
  "Sin compromiso · Totalmente gratis",
];

export default function Footer() {
  const [branches, setBranches] = useState([]);
  const [waPickerOpen, setWaPickerOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    branch: "",
    visit_date: "",
    visit_time: "morning",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
    const onPreselect = (e) => {
      const { branch } = e.detail || {};
      if (branch) setForm((f) => ({ ...f, branch }));
    };
    window.addEventListener("ie:preselect", onPreselect);
    return () => window.removeEventListener("ie:preselect", onPreselect);
  }, []);

  const minDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1); // tomorrow earliest
    return d.toISOString().split("T")[0];
  }, []);

  const selectedBranch = useMemo(
    () => branches.find((b) => b.name === form.branch),
    [branches, form.branch]
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.branch || !form.visit_date) {
      toast.error("Completa todos los campos para programar tu visita.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", { ...form, type: "visit" });
      toast.success("¡Visita registrada! Te conectamos con la sucursal por WhatsApp.");

      // Auto-open WhatsApp with the branch
      if (selectedBranch) {
        const dateLabel = new Date(form.visit_date + "T00:00:00").toLocaleDateString("es-MX", {
          weekday: "long",
          day: "numeric",
          month: "long",
        });
        const timeLabel = form.visit_time === "morning" ? "por la mañana (10:00 - 13:00)" : "por la tarde (16:00 - 19:00)";
        const msg = encodeURIComponent(
          `Hola ${selectedBranch.name}, programé una visita el ${dateLabel} ${timeLabel}. Mi nombre es ${form.name}. Quisiera conocer las instalaciones.`
        );
        window.open(
          `https://wa.me/${selectedBranch.whatsapp}?text=${msg}`,
          "_blank",
          "noopener"
        );
      }
      setForm({ name: "", phone: "", branch: "", visit_date: "", visit_time: "morning" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo agendar.");
    } finally {
      setLoading(false);
    }
  };

  const handleWAPickerSelect = (branch) => {
    const msg = encodeURIComponent(
      `Hola ${branch.name}, quiero información sobre los cursos del Instituto Europa.`
    );
    window.open(`https://wa.me/${branch.whatsapp}?text=${msg}`, "_blank", "noopener");
    setWaPickerOpen(false);
  };

  return (
    <footer
      id="contacto"
      className="bg-[#0a0a0a] text-white"
      data-testid="footer-section"
    >
      {/* Visit scheduling */}
      <div className="container-ie pt-24 pb-16 md:py-20 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        {/* Left: pitch */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-6"
        >
          <div className="flex items-center gap-3 mb-5">
            <span className="dot-red" />
            <span className="overline" style={{ color: "#c40000" }}>
              Visita guiada · Sin costo
            </span>
          </div>
          <h3 className="font-display font-semibold tracking-tighter text-4xl md:text-5xl lg:text-[3.4rem] leading-[1.05]">
            Programa una visita a nuestras
            <span className="italic font-light text-white/70"> instalaciones.</span>
          </h3>
          <p className="mt-6 max-w-xl text-base md:text-lg font-light text-white/70 leading-relaxed">
            La mejor forma de elegir tu academia es conocerla. Te recibimos en la sucursal de tu preferencia, te mostramos las aulas y resolvemos todas tus dudas en persona.
          </p>

          <ul className="mt-8 space-y-3" data-testid="visit-perks">
            {visitPerks.map((p) => (
              <li key={p} className="flex items-start gap-3 text-sm text-white/85">
                <span className="mt-0.5 w-5 h-5 flex items-center justify-center border border-[#c40000]/60 shrink-0">
                  <Check size={11} strokeWidth={2.4} className="text-[#c40000]" />
                </span>
                <span>{p}</span>
              </li>
            ))}
          </ul>

          <div className="mt-10 pt-8 border-t border-white/10">
            <div className="text-xs uppercase tracking-[0.18em] text-white/50 mb-3">
              ¿Prefieres chatear primero?
            </div>
            <button
              type="button"
              data-testid="footer-wa-picker-open"
              onClick={() => setWaPickerOpen(true)}
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#25d366] hover:text-white transition-colors"
            >
              <MessageCircle size={16} strokeWidth={1.6} /> Habla por WhatsApp con tu sucursal
            </button>
          </div>
        </motion.div>

        {/* Right: form card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="lg:col-span-6"
        >
          <div className="bg-white text-[#0a0a0a] p-7 md:p-9">
            <div className="flex items-center gap-2 mb-5">
              <CalendarDays size={18} strokeWidth={1.6} className="text-[#c40000]" />
              <span className="text-xs uppercase tracking-[0.2em] font-semibold">
                Programa tu visita
              </span>
            </div>

            <form onSubmit={submit} className="space-y-4" data-testid="visit-form">
              <input
                data-testid="visit-name"
                className="ie-input"
                placeholder="Nombre completo"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
              <input
                data-testid="visit-phone"
                className="ie-input"
                placeholder="WhatsApp / Teléfono"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                required
              />
              <select
                data-testid="visit-branch"
                className="ie-input"
                value={form.branch}
                onChange={(e) => setForm({ ...form, branch: e.target.value })}
                required
              >
                <option value="">Sucursal a visitar</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.name}>
                    {b.name}
                  </option>
                ))}
              </select>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="relative">
                  <CalendarDays
                    size={14}
                    strokeWidth={1.6}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500 pointer-events-none"
                  />
                  <input
                    data-testid="visit-date"
                    type="date"
                    min={minDate}
                    className="ie-input"
                    style={{ paddingLeft: "2.25rem" }}
                    value={form.visit_date}
                    onChange={(e) => setForm({ ...form, visit_date: e.target.value })}
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-0 border border-black/12">
                  <button
                    type="button"
                    data-testid="visit-time-morning"
                    onClick={() => setForm({ ...form, visit_time: "morning" })}
                    className={`flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] transition-colors py-3 ${
                      form.visit_time === "morning"
                        ? "bg-[#0a0a0a] text-white"
                        : "bg-white text-[#0a0a0a] hover:bg-[#fafafa]"
                    }`}
                  >
                    <Sun size={14} strokeWidth={1.6} /> Mañana
                  </button>
                  <button
                    type="button"
                    data-testid="visit-time-afternoon"
                    onClick={() => setForm({ ...form, visit_time: "afternoon" })}
                    className={`flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] transition-colors py-3 ${
                      form.visit_time === "afternoon"
                        ? "bg-[#0a0a0a] text-white"
                        : "bg-white text-[#0a0a0a] hover:bg-[#fafafa]"
                    }`}
                  >
                    <Sunset size={14} strokeWidth={1.6} /> Tarde
                  </button>
                </div>
              </div>

              <button
                data-testid="visit-submit"
                type="submit"
                disabled={loading}
                className="btn-primary w-full justify-center disabled:opacity-60"
                style={{ background: "#c40000", borderColor: "#c40000" }}
              >
                {loading ? "Programando..." : "Programar visita"}
              </button>
              <p className="text-[0.7rem] text-[#525252] leading-relaxed text-center">
                Al programar abriremos WhatsApp con la sucursal seleccionada para confirmar tu visita.
              </p>
            </form>
          </div>
        </motion.div>
      </div>

      {/* Locator: branch selector + nearest finder + map */}
      <div className="border-t border-white/10">
        <BranchLocator />
        <div className="container-ie pb-8 flex flex-wrap items-center justify-end gap-6">
          <div className="flex items-center gap-3">
            <a
              data-testid="footer-social-instagram"
              href="#"
              aria-label="Instagram"
              className="w-11 h-11 border border-white/15 flex items-center justify-center hover:bg-white hover:text-[#0a0a0a] transition-colors"
            >
              <Instagram size={18} strokeWidth={1.5} />
            </a>
            <a
              data-testid="footer-social-facebook"
              href="#"
              aria-label="Facebook"
              className="w-11 h-11 border border-white/15 flex items-center justify-center hover:bg-white hover:text-[#0a0a0a] transition-colors"
            >
              <Facebook size={18} strokeWidth={1.5} />
            </a>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="container-ie py-6 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-white/50">
          <div className="flex items-center gap-2">
            <span className="dot-red" />
            <span className="font-semibold uppercase tracking-[0.18em]">
              Instituto <span className="text-white">Europa</span>
            </span>
          </div>
          <div className="flex items-center gap-6">
            <span>© {new Date().getFullYear()} — Todos los derechos reservados.</span>
            <a href="/admin/login" data-testid="footer-admin-link" className="hover:text-white">
              Admin
            </a>
          </div>
        </div>
      </div>

      <BranchPickerModal
        open={waPickerOpen}
        mode="whatsapp"
        course={null}
        onClose={() => setWaPickerOpen(false)}
        onSelect={handleWAPickerSelect}
      />
    </footer>
  );
}
